import { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { useTranslations } from 'use-intl';

import { getPanelIdAndProvider, type ResponderPanel } from '@/shared-survey';
import { ADD_FORM_INSIGHT, type Channel } from '@/survey-graphql';
import { toast } from '@/ui';

interface UseSurveyAiFormsSubmissionInsightProps {
  projectId: string;
  sessionId: string;
  isPreviewMode: boolean;
  channel?: Channel;
  panel?: ResponderPanel | null;
}

export function useSurveyAiFormsSubmissionInsight({
  projectId,
  sessionId,
  isPreviewMode,
  channel,
  panel = null,
}: UseSurveyAiFormsSubmissionInsightProps) {
  const t = useTranslations('AIForms.toast');
  const [insightId, setInsightId] = useState<string | null>(null);

  const [addFormInsight, { loading: isAddingInsight }] =
    useMutation(ADD_FORM_INSIGHT);

  const ensureInsightId = async (): Promise<string | null> => {
    if (isPreviewMode) {
      return null;
    }

    if (insightId) {
      return insightId;
    }

    try {
      const result = await addFormInsight({
        variables: {
          input: {
            projectId,
            sessionId,
            channel,
            ...(panel && getPanelIdAndProvider(panel)),
          },
        },
      });

      const newInsightId = result.data?.addFormInsight?.id;
      if (newInsightId) {
        setInsightId(newInsightId);
        return newInsightId;
      }
      return null;
    } catch (error: unknown) {
      toast.add({
        title: error instanceof Error ? error.message : t('genericError'),
        type: 'error',
      });
      return null;
    }
  };

  return {
    insightId,
    setInsightId,
    ensureInsightId,
    isAddingInsight,
  };
}
